import { FieldErrors, FieldError, FieldValues } from 'react-hook-form';
import _get from 'lodash/get';
import { FieldItem } from '@lib/interfaces/form';
import { Steps } from '@lib/enums/form';

const getErrorsByStep = (
  fields: FieldItem[],
  errors: FieldErrors<FieldValues>,
): Record<number, FieldError[]> => {
  const errorsByStep: Record<number, FieldError[]> = {};

  const addErrorToStep = (step: number, error: FieldError) => {
    if (!errorsByStep[step]) {
      errorsByStep[step] = [];
    }
    errorsByStep[step].push(error);
  };

  const processError = (
    step?: Steps,
    fieldError?: FieldError | FieldError[],
  ) => {
    if (step && fieldError) {
      if (Array.isArray(fieldError)) {
        fieldError.forEach((nestedError) => processError(step, nestedError));
      } else if (typeof fieldError === 'object') {
        // If the error object itself could contain nested errors, recursively process them
        if ('message' in fieldError && 'type' in fieldError) {
          // It's a FieldError object
          addErrorToStep(step, fieldError);
        } else {
          // If it's not a direct FieldError, it might be an object with nested FieldError(s)
          Object.values(fieldError).forEach((nestedError) =>
            processError(step, nestedError as FieldError),
          );
        }
      }
    }
  };

  // Same default as getFormData: a field without `step` renders on step 1.
  const processFieldError = (field: FieldItem, fallbackStep: Steps) => {
    const { step = fallbackStep, name } = field;
    // _get handles dotted names; nested errors are walked by processError
    const fieldError = _get(errors, name);
    processError(step, fieldError as FieldError);
  };

  fields.forEach((field) => {
    if (Array.isArray(field.groupFields)) {
      // Group children render on the group's step unless they set their own
      field.groupFields.forEach((groupField) =>
        processFieldError(groupField, field.step ?? Steps.step1),
      );
    } else {
      processFieldError(field, Steps.step1);
    }
  });

  return errorsByStep;
};

export default getErrorsByStep;
